import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Download, History, Loader2, RefreshCw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import TableColumnFilter, {
  passesColumnFilter,
  type ColumnFilterValue,
} from "@/components/TableColumnFilter";
import { useToast } from "@/hooks/use-toast";
import { validateExcelImportFile } from "@/lib/excelImport";
import {
  XeroStatusError,
  XeroStatusLoading,
} from "@/components/admin/XeroStatusStates";
import XeroOrganisationRow from "@/components/admin/XeroOrganisationRow";
import {
  XERO_STATUS_QUERY_KEY,
  XERO_ORG_RULES_QUERY_KEY,
  bulkImportXeroOrgRules,
  exportXeroOrgRules,
  getXeroStatus,
  getXeroOrgRules,
  type XeroOrgRulesSummary,
} from "@/services/xeroApi";

function OrgRulesErrorBanner({ data }: { data: XeroOrgRulesSummary }) {
  if (!data.org_rules_error) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p>{data.org_rules_error}</p>
    </div>
  );
}

export default function XeroOrganisationsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: XERO_STATUS_QUERY_KEY,
    queryFn: getXeroStatus,
  });


  const rules = useQuery({
    queryKey: XERO_ORG_RULES_QUERY_KEY,
    queryFn: getXeroOrgRules,
    enabled: status.data?.connected === true,
    refetchInterval: 15_000,
  });

  const [tenantNameFilter, setTenantNameFilter] = useState<ColumnFilterValue>(null);
  const [takenFromFilter, setTakenFromFilter] = useState<ColumnFilterValue>(null);

  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bulkImport = useMutation({
    mutationFn: bulkImportXeroOrgRules,
    onSuccess: (result) => {
      toast({
        title: "Organisations imported",
        description:
          result.updated_count === 0
            ? "The file matched what Prism already holds, so nothing changed."
            : `${result.updated_count} organisation(s) updated.`,
      });
      queryClient.invalidateQueries({ queryKey: XERO_ORG_RULES_QUERY_KEY });
    },
    onError: (e) => {
      toast({
        variant: "destructive",
        title: "Couldn’t import the file",
        description: (e as Error).message,
      });
    },
  });

  const handleImportClick = () => {
    if (bulkImport.isPending) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const problem = validateExcelImportFile(file);
    if (problem) {
      toast({ variant: "destructive", title: "Couldn’t import the file", description: problem });
      return;
    }
    bulkImport.mutate(file);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportXeroOrgRules();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Couldn’t export the organisations",
        description: (e as Error).message,
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (status.isLoading || (status.data?.connected && rules.isLoading)) {
    return <XeroStatusLoading />;
  }

  if (status.isError) {
    return (
      <XeroStatusError
        message={(status.error as Error)?.message}
        onRetry={() => status.refetch()}
      />
    );
  }

  if (!status.data?.connected) {
    return (
      <p className="text-sm text-muted-foreground">
        Xero isn’t connected. Set it up on the Connection tab.
      </p>
    );
  }

  if (rules.isError) {
    return (
      <XeroStatusError
        message={(rules.error as Error)?.message}
        onRetry={() => rules.refetch()}
      />
    );
  }

  const organisations = rules.data?.organisations ?? [];

  const tenantNameValues = organisations.map((o) => o.tenant_name ?? "");
  const takenFromValues = organisations.map((o) => o.taken_from ?? "");

  const filtered = organisations.filter(
    (o) =>
      passesColumnFilter(tenantNameFilter, o.tenant_name ?? "") &&
      passesColumnFilter(takenFromFilter, o.taken_from ?? ""),
  );

  const isFiltered = tenantNameFilter !== null || takenFromFilter !== null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">
          Organisations
          {organisations.length > 0 && (
            <span className="ml-2 font-normal text-muted-foreground">
              {isFiltered ? `${filtered.length} of ${organisations.length}` : organisations.length}{" "}
              {organisations.length === 1 ? "organisation" : "organisations"}
            </span>
          )}
          {isFiltered && (
            <button
              type="button"
              className="ml-2 text-xs font-normal text-primary hover:underline"
              onClick={() => {
                setTenantNameFilter(null);
                setTakenFromFilter(null);
              }}
            >
              Clear filters
            </button>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => rules.refetch()}
            disabled={rules.isFetching}
            title="Refresh"
            aria-label="Refresh organisations"
          >
            <RefreshCw className={`h-4 w-4 ${rules.isFetching ? "animate-spin" : ""}`} />
          </Button>
          {/* Opens in this browser tab; the log page's Back link (or the
              browser's Back button) returns to this tab. */}
          <Button size="sm" variant="outline" className="gap-2" asChild>
            <Link to="/datacore/xero/organisations/logs">
              <History className="h-4 w-4" />
              Logs
            </Link>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={handleFileSelected}
          />
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={handleImportClick}
            disabled={bulkImport.isPending}
          >
            {bulkImport.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {bulkImport.isPending ? "Importing…" : "Import from Excel"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={handleExport}
            disabled={isExporting || organisations.length === 0}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExporting ? "Exporting…" : "Export to Excel"}
          </Button>
        </div>
      </div>

      {rules.data && <OrgRulesErrorBanner data={rules.data} />}

      {organisations.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          The consent granted no organisations. Reconnect and select at least
          one.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            Entity, retrieval date and pre-2022 source are recorded in Prism
            only and are not written back to Xero. All three are published to
            Fabric's org_rules table, which the notebooks read instead of
            hardcoded SQL — journals dated before the retrieval date are left
            out of that organisation's consolidated data. This grid reads Fabric's own copy, so a save appears here
            once the publish and Fabric's sync have both caught up, not
            immediately.
          </p>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead>
                    <span className="inline-flex items-center gap-1">
                      Organisation
                      <TableColumnFilter
                        label="Organisation"
                        values={tenantNameValues}
                        selected={tenantNameFilter}
                        onChange={setTenantNameFilter}
                      />
                    </span>
                  </TableHead>
                  <TableHead className="w-[220px]">Entity</TableHead>
                  <TableHead className="w-[170px]">Retrieval date</TableHead>
                  <TableHead className="w-[220px]">Pre-2022 source</TableHead>
                  <TableHead className="w-[600px]">Legacy office</TableHead>
                  <TableHead className="w-[130px]">Required in cube</TableHead>
                  <TableHead className="w-[160px]">
                    <span className="inline-flex items-center gap-1">
                      Taken from
                      <TableColumnFilter
                        label="Taken from"
                        values={takenFromValues}
                        selected={takenFromFilter}
                        onChange={setTakenFromFilter}
                      />
                    </span>
                  </TableHead>
                  <TableHead className="w-[170px]">Updated (Fabric)</TableHead>
                  <TableHead className="w-[170px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-sm text-muted-foreground">
                      No organisations match the current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((organisation) => (
                    <XeroOrganisationRow
                      key={`${organisation.row_id ?? ""}:${organisation.tenant_name}:${organisation.entity ?? ""}:${organisation.retrieval_date ?? ""}:${organisation.pre2022_source ?? ""}:${organisation.default_legacy_office ?? ""}:${organisation.required_in_cube}`}
                      organisation={organisation}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
